import { ConversationLifecycleEvent } from './types';

export interface ConversationLifecycleState {
  sessionId: string | null;
  byEventId: Record<string, ConversationLifecycleEvent>;
  latestByTurnId: Record<string, ConversationLifecycleEvent>;
  cursor: number;
  synchronized: boolean;
  gapDetected: boolean;
}

export const initialLifecycleState = (sessionId: string | null = null): ConversationLifecycleState => ({
  sessionId,
  byEventId: {},
  latestByTurnId: {},
  cursor: 0,
  synchronized: false,
  gapDetected: false,
});

export const reduceLifecycleEvents = (
  state: ConversationLifecycleState,
  incoming: ConversationLifecycleEvent[],
): ConversationLifecycleState => {
  const ordered = [...incoming].sort((a, b) => a.sequence - b.sequence);
  const next: ConversationLifecycleState = {
    sessionId: state.sessionId,
    byEventId: { ...state.byEventId },
    latestByTurnId: { ...state.latestByTurnId },
    cursor: state.cursor,
    synchronized: state.synchronized,
    gapDetected: state.gapDetected,
  };

  for (const event of ordered) {
    if (next.sessionId && event.sessionId !== next.sessionId) continue;
    if (next.byEventId[event.eventId]) continue;
    if (event.sequence <= next.cursor) continue;
    if (event.sequence > next.cursor + 1) {
      next.gapDetected = true;
      break;
    }
    next.sessionId ??= event.sessionId;
    next.byEventId[event.eventId] = event;
    const current = next.latestByTurnId[event.conversationTurnId];
    if (!current || event.sequence > current.sequence) next.latestByTurnId[event.conversationTurnId] = event;
    next.cursor = Math.max(next.cursor, event.sequence);
  }
  return next;
};

export const latestLifecycleEvent = (state: ConversationLifecycleState): ConversationLifecycleEvent | null =>
  Object.values(state.latestByTurnId).sort((a, b) => b.sequence - a.sequence)[0] ?? null;
